/**
 * Admin端文件上传工具类
 * 复制并适配uniapp/src/services/wechat-common-service.ts中的WechatCommonApiClient
 * 使用与UniApp相同的架构，适配Web端环境
 */

import axios, { AxiosProgressEvent, AxiosRequestConfig } from "axios";
import { API_CONFIG } from "../config/api.config";

// 文件上传相关类型定义（与UniApp保持一致）
export interface GetUploadCredentialsRequest {
  filename: string;
  filePath?: string;
  fileType?: string;
  fileSize?: number;
}

export interface UploadCredentialsResponse {
  secretId: string;
  secretKey: string;
  sessionToken: string;
  startTime: number;
  expiredTime: number;
  bucket: string;
  region: string;
  domain: string;
  preSignedUrl?: string;
}

export interface UploadProgressCallback {
  (progress: { percent: number; loaded: number; total: number }): void;
}

// API配置接口（复制自UniApp）
interface WechatCommonApiConfig {
  baseURL: string;
  timeout?: number;
  headers?: Record<string, string>;
}

interface WechatCommonRequestConfig {
  url: string;
  method?: "GET" | "POST" | "PUT" | "DELETE";
  data?: any;
  params?: Record<string, any>;
  headers?: Record<string, string>;
  timeout?: number;
  useWechatToken?: boolean;
}

/**
 * Admin端API客户端（复制并适配自UniApp的WechatCommonApiClient）
 */
class AdminApiClient {
  private config: WechatCommonApiConfig;

  constructor(config: WechatCommonApiConfig) {
    this.config = {
      timeout: 10000,
      ...config,
    };
  }

  private getAuthToken(): string | null {
    return localStorage.getItem("token") || null;
  }

  private buildHeaders(
    config: WechatCommonRequestConfig
  ): Record<string, string> {
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
      Accept: "application/json",
      ...this.config.headers,
      ...config.headers,
    };

    if (config.useWechatToken !== false) {
      const authToken = this.getAuthToken();
      if (authToken) {
        headers["Authorization"] = `Bearer ${authToken}`;
      }
    }

    return headers;
  }

  private buildUrl(url: string, params?: Record<string, any>): string {
    const fullUrl = `${this.config.baseURL}${url}`;

    if (!params || Object.keys(params).length === 0) {
      return fullUrl;
    }

    const queryString = Object.keys(params)
      .filter(
        (key) =>
          params[key] !== undefined &&
          params[key] !== null &&
          params[key] !== ""
      )
      .map(
        (key) => `${encodeURIComponent(key)}=${encodeURIComponent(params[key])}`
      )
      .join("&");

    return queryString ? `${fullUrl}?${queryString}` : fullUrl;
  }

  private handleResponse<T>(response: any): Promise<T> {
    const { status, data } = response;

    if (status >= 200 && status < 300) {
      return Promise.resolve(data as T);
    }

    const error = new Error(`HTTP Error: ${status}`);
    (error as any).statusCode = status;
    (error as any).errorData = data;

    return Promise.reject(error);
  }

  private handleError(error: any): Promise<never> {
    console.error("Admin API 请求错误:", error);
    return Promise.reject(error);
  }

  public async request<T>(config: WechatCommonRequestConfig): Promise<T> {
    try {
      const url = this.buildUrl(config.url, config.params);
      const headers = this.buildHeaders(config);

      const axiosConfig: AxiosRequestConfig = {
        url,
        method: config.method || "GET",
        headers,
        timeout: config.timeout || this.config.timeout,
      };

      if (config.method !== "GET" && config.method !== "DELETE") {
        axiosConfig.data = config.data;
      }

      const response = await axios(axiosConfig);
      return this.handleResponse<T>(response);
    } catch (error) {
      throw await this.handleError(error);
    }
  }

  post<T>(
    url: string,
    data?: any,
    options?: Partial<WechatCommonRequestConfig>
  ): Promise<T> {
    return this.request<T>({
      ...options,
      url,
      method: "POST",
      data,
    });
  }
}

// 创建Admin端API客户端实例
const wechatCommonApiClient = new AdminApiClient({
  baseURL: API_CONFIG.WECHAT_COMMON_BASE_URL,
  timeout: API_CONFIG.TIMEOUT,
  headers: {
    Accept: "application/json",
  },
});

/**
 * Admin端文件上传API工具类
 */
export const adminFileUploadAPI = {
  isValidFile(file: File): boolean {
    return !!(file && file instanceof File && file.size > 0);
  },

  getUploadCredentials(
    params: GetUploadCredentialsRequest
  ): Promise<UploadCredentialsResponse> {
    return wechatCommonApiClient.post<UploadCredentialsResponse>(
      "/files/get-credentials",
      params,
      {
        useWechatToken: false, // 文件上传凭证通常不需要用户认证
      }
    );
  },

  async uploadImage(
    file: File,
    uploadParams: {
      filename?: string;
      filePath?: string;
    } = {},
    onProgress?: UploadProgressCallback
  ): Promise<string> {
    try {
      if (!this.isValidFile(file)) {
        throw new Error("无效的文件对象");
      }

      const fileSize = file.size;
      const timestamp = Date.now();
      const randomSuffix = Math.random().toString(36).substring(2, 8);
      const fileExtension = file.name.split(".").pop() || "jpg";
      const filename =
        uploadParams.filename ||
        `image_${timestamp}_${randomSuffix}.${fileExtension}`;

      const credentials = await this.getUploadCredentials({
        filename,
        filePath: uploadParams.filePath || "admin-uploads/",
        fileType: file.type || `image/${fileExtension}`,
        fileSize,
      });

      if (credentials.preSignedUrl) {
        const preSignedData = JSON.parse(credentials.preSignedUrl);
        const uploadUrl =
          preSignedData.url ||
          `https://${credentials.bucket}.cos.${credentials.region}.myqcloud.com/`;

        const formData = new FormData();

        if (preSignedData.key) {
          formData.append("key", preSignedData.key);
        }

        const cosFields = [
          "policy",
          "q-sign-algorithm",
          "q-ak",
          "q-key-time",
          "q-signature",
          "q-header-list",
          "q-url-param-list",
          "x-cos-security-token",
          "success_action_status",
        ];

        cosFields.forEach((field) => {
          if (preSignedData[field] !== undefined) {
            formData.append(field, preSignedData[field]);
          }
        });

        formData.append("file", file);

        const headers: Record<string, string> = {};

        if (preSignedData.token) {
          headers["x-cos-security-token"] = preSignedData.token;
        }

        if (preSignedData.authorization) {
          headers["Authorization"] = preSignedData.authorization;
        }

        const response = await axios.post(uploadUrl, formData, {
          headers,
          onUploadProgress: (progressEvent: AxiosProgressEvent) => {
            if (onProgress && progressEvent.total) {
              const percent = Math.round(
                (progressEvent.loaded * 100) / progressEvent.total
              );
              onProgress({
                percent,
                loaded: progressEvent.loaded,
                total: progressEvent.total,
              });
            }
          },
        });

        if (response.status >= 200 && response.status < 300) {
          const fileUrl = `${credentials.domain}/${preSignedData.key}`;
          return fileUrl;
        } else {
          throw new Error(`上传失败: ${response.status}`);
        }
      } else {
        throw new Error("未获取到预签名URL，无法上传文件");
      }
    } catch (error) {
      console.error("图片上传失败:", error);
      if (error instanceof Error) {
        throw error;
      } else {
        throw new Error("图片上传失败，请重试");
      }
    }
  },

  async uploadAvatar(
    file: File,
    onProgress?: UploadProgressCallback
  ): Promise<string> {
    return this.uploadImage(
      file,
      {
        filePath: "avatars/",
      },
      onProgress
    );
  },

  validateFile(
    file: File,
    options: {
      allowedTypes?: string[];
      maxSizeInMB?: number;
    } = {}
  ): { valid: boolean; error?: string } {
    const { allowedTypes, maxSizeInMB = 10 } = options;

    if (!this.isValidFile(file)) {
      return { valid: false, error: "无效的文件对象" };
    }

    if (allowedTypes && !allowedTypes.includes(file.type)) {
      return { valid: false, error: "不支持的文件类型" };
    }

    const maxSizeInBytes = maxSizeInMB * 1024 * 1024;
    if (file.size > maxSizeInBytes) {
      return { valid: false, error: `文件大小不能超过 ${maxSizeInMB}MB` };
    }

    return { valid: true };
  },
};

export { wechatCommonApiClient as adminApiClient };
export default adminFileUploadAPI;
